import React from "react";

import {
  Box,
  Card,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import HelpIcon from '@mui/icons-material/Help';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

import KeyBoard from "../../assets/keyboard.png";
import WithPopUp from "../../assets/WithPopUp.png";
import BlackScreen from "../../assets/BlackScreen.png";
import RealWearActionButton from "../../assets/RealWearActionButton.jpg";
import StyledAccordion, { AccordionGroup, HelpCategory, helpCategoryConfig } from "./components/styled-accordion";
import MainLayout from "../../layouts/main-layout";
import CaptionedImage from "./components/captioned-image";
import NumberedStep from "../../components/numbered-step";
import UiActionLabel from "./components/ui-action-label";
import VoiceCommandLabel from "./components/voice-command-label";
import RoleChip from "../../components/chips/role-chip";
import NoteBox from "./components/note-box";

import { useGetCurrentUser } from "../../store";
import { userHasPermission } from "../../types-helpers";
import { UserType } from "../../types";

const HelpPage: React.FC = () => {
  const supportEmail = "clearcall@ivai.com.au";

  const { data: currentUser } = useGetCurrentUser();

  const isClientAdmin = userHasPermission(UserType.SysAdmin, currentUser);
  const isTeamAdmin =
    isClientAdmin || Boolean(currentUser?.adminIds.length);

  const [selectedCategory, setSelectedCategory] = React.useState<HelpCategory | "all">("all");

  const isCategoryVisible = (category: HelpCategory) =>
    selectedCategory === "all" || selectedCategory === category;

  return (
    <MainLayout>
      <Stack spacing={2} pb={4}>
        <Card
          sx={{
            p: 2.5,
            flex: 1,
            display: "flex",
            alignItems: "center",
            gap: 2,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "primary.main",
            backgroundColor: "#eef6fc",
          }}
        >
          <HelpIcon sx={{ fontSize: 40, color: "primary.main" }} />
          <Stack spacing={0.25}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, color: "text.secondary" }}
            >
              If you can't resolve your problem with the information below, contact us at:
            </Typography>
            <Typography
              variant="h5"
              component="a"
              href={`mailto:${supportEmail}`}
              sx={{
                fontWeight: 600,
                color: "primary.main",
                textDecoration: "none",
                wordBreak: "break-all",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {supportEmail}
            </Typography>
          </Stack>
        </Card>
        <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
          <Chip
            label="All"
            onClick={() => setSelectedCategory("all")}
            color={selectedCategory === "all" ? "primary" : "default"}
            variant={selectedCategory === "all" ? "filled" : "outlined"}
          />
          {(Object.entries(helpCategoryConfig) as [HelpCategory, typeof helpCategoryConfig[HelpCategory]][]).map(
            ([category, config]) => {
              const CategoryIcon = config.icon;
              const selected = selectedCategory === category;

              return (
                <Chip
                  key={category}
                  icon={<CategoryIcon sx={{ fontSize: "1rem" }} />}
                  label={config.label}
                  onClick={() => setSelectedCategory(category)}
                  color={selected ? "primary" : "default"}
                  variant={selected ? "filled" : "outlined"}
                  sx={{ pl: .5 }}
                />
              );
            }
          )}
        </Stack>
        <Card sx={{ backgroundColor: "#F5F5F5", mb: 2 }}>
          <AccordionGroup>
            {isCategoryVisible("website") && (
              <>
                <StyledAccordion title="How do I create a meeting?" categories={["website"]}>
                  <NumberedStep number={1}>
                    Go to the Calendar page and click the <UiActionLabel>Schedule Meeting</UiActionLabel> button in the toolbar.
                  </NumberedStep>
                  <NumberedStep number={2}>
                    {`Select the patient the meeting is for.`}
                  </NumberedStep>
                  <NumberedStep number={3}>
                    {`Choose the attendees for the meeting. You can add existing users or invite someone new or external by email.`}
                  </NumberedStep>
                  <NumberedStep number={4}>
                    {`Pick a start time, currently only single time selection is supported and meetings are pre-set to 30 minutes (however you can stay longer if needed).`}
                  </NumberedStep>
                  <NumberedStep number={5}>
                    Click <UiActionLabel>Schedule Meeting</UiActionLabel> to confirm and add it to the calendar.
                  </NumberedStep>
                </StyledAccordion>
                <StyledAccordion title="How do I join a meeting?" categories={["website", "headset"]}>
                  <Typography variant="body1">
                    To join with the <Box component="span" sx={{ fontWeight: 700 }}>website</Box>:
                  </Typography>
                  <NumberedStep number={1}>
                    Go to the Calendar page and find the meeting you want to join.
                  </NumberedStep>
                  <NumberedStep number={2}>
                    Click on the meeting to open its details.
                  </NumberedStep>
                  <NumberedStep number={3}>
                    Click <UiActionLabel>Join Meeting</UiActionLabel> to enter the call. If more than one layout is available for your team, you will be asked to select one first.
                  </NumberedStep>
                  <Typography variant="body1">
                    To join with the <Box component="span" sx={{ fontWeight: 700 }}>headset</Box>:
                  </Typography>
                  <NumberedStep number={1}>
                    Join the meeting on your workstation via the website.
                  </NumberedStep>
                  <NumberedStep number={2}>
                    A QR code will be shown on screen (toggle it with the QR code button in the toolbar if it isn't visible).
                  </NumberedStep>
                  <NoteBox>QR codes are only displayed in the <Box component="span" sx={{ fontWeight: 700 }}>Remote Layout</Box>.</NoteBox>
                  <NumberedStep number={3}>
                    On your headset, open the Clear Call App (otherwise the Barcode Reader App) and scan the QR code displayed on your workstation.
                  </NumberedStep>
                  <NumberedStep number={4}>
                    Wait for the headset to recognize the QR code and establish a connection.
                  </NumberedStep>
                  <NumberedStep number={6}>
                    The headset will connect automatically, and its camera feed will replace the workstation's video in the call.
                  </NumberedStep>
                </StyledAccordion>
                {isTeamAdmin && (
                  <StyledAccordion title="How do I add a member to my team?" categories={["website"]}>
                    <Typography variant="body1" component="div" sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                      This is only visible to <RoleChip role="Team Admin" /> and <RoleChip role="Client Admin" />.
                    </Typography>
                    <NumberedStep number={1}>
                      {`Go to the Teams page and select your team, or navigate to it from the Manage button.`}
                    </NumberedStep>
                    <NumberedStep number={2}>
                      On the Members tab, click <UiActionLabel>Add Member</UiActionLabel>.
                    </NumberedStep>
                    <NumberedStep number={3}>
                      {`Select the user you want to add from the list and confirm.`}
                    </NumberedStep>
                  </StyledAccordion>
                )}
                {isClientAdmin && (
                  <StyledAccordion title="How do I add a new user to the client?" categories={["website"]}>
                    <Typography variant="body1" component="div" sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                      This is only visible to <RoleChip role="Client Admin" />.
                    </Typography>
                    <NumberedStep number={1}>
                      Go to the Users page and click <UiActionLabel>Invite User</UiActionLabel>.
                    </NumberedStep>
                    <NumberedStep number={2}>
                      {`Enter the new user's email address and select which team(s) they should belong to.`}
                    </NumberedStep>
                    <NumberedStep number={3}>
                      {`Submit the form to send them an invitation email.`}
                    </NumberedStep>
                  </StyledAccordion>
                )}
                {(isClientAdmin || isTeamAdmin || Boolean(currentUser)) && (
                  <StyledAccordion title="How do I add a patient?" categories={["website"]}>
                    <Typography variant="body1" component="div" sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                      This is available to <RoleChip role="Member" />, <RoleChip role="Team Admin" />, and <RoleChip role="Client Admin" />.
                    </Typography>
                    <NumberedStep number={1}>
                      Go to the Patients page and click <UiActionLabel>Add Patient</UiActionLabel>.
                    </NumberedStep>
                    <NumberedStep number={2}>
                      Confirm in the dialog that appears. A new patient record is created straight away, with a randomly generated 5-character patient ID.
                    </NumberedStep>
                    <NoteBox>
                      Clear Call does not store any patient personal information, such as a name or date of birth, only this generated ID. It is important that you keep track of which ID belongs to which patient, as this is currently the only way to identify them within the system.
                    </NoteBox>
                  </StyledAccordion>
                )}
                <StyledAccordion title="What permissions does each role have?" categories={["website"]}>
                  <Typography variant="body1" sx={{ mb: 1.5 }}>
                    Roles control what a user can see and manage within a client or team.
                  </Typography>
                  <TableContainer sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, backgroundColor: "#fff" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, width: "36%" }}>Action</TableCell>
                          <TableCell align="center"><RoleChip role="Client Admin" /></TableCell>
                          <TableCell align="center"><RoleChip role="Team Admin" /></TableCell>
                          <TableCell align="center"><RoleChip role="Member" /></TableCell>
                          <TableCell align="center"><RoleChip role="Guest" /></TableCell>
                          <TableCell align="center"><RoleChip role="Blocked" /></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>Join Meetings</TableCell>
                          <TableCell align="center"><Box component="span" sx={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width: 24, height: 24, borderRadius: "50%", backgroundColor: "#e8f7ee", color: "#1f9d61" }}><CheckCircleIcon sx={{ fontSize: 18 }} /></Box></TableCell>
                          <TableCell align="center"><Box component="span" sx={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width: 24, height: 24, borderRadius: "50%", backgroundColor: "#e8f7ee", color: "#1f9d61" }}><CheckCircleIcon sx={{ fontSize: 18 }} /></Box></TableCell>
                          <TableCell align="center"><Box component="span" sx={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width: 24, height: 24, borderRadius: "50%", backgroundColor: "#e8f7ee", color: "#1f9d61" }}><CheckCircleIcon sx={{ fontSize: 18 }} /></Box></TableCell>
                          <TableCell align="center"><Box component="span" sx={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width: 24, height: 24, borderRadius: "50%", backgroundColor: "#e8f7ee", color: "#1f9d61" }}><CheckCircleIcon sx={{ fontSize: 18 }} /></Box></TableCell>
                          <TableCell align="center"><Box component="span" sx={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width: 24, height: 24, borderRadius: "50%", backgroundColor: "#fdecec", color: "#c62828" }}><CancelIcon sx={{ fontSize: 18 }} /></Box></TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Add patients</TableCell>
                          <TableCell align="center"><Box component="span" sx={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width: 24, height: 24, borderRadius: "50%", backgroundColor: "#e8f7ee", color: "#1f9d61" }}><CheckCircleIcon sx={{ fontSize: 18 }} /></Box></TableCell>
                          <TableCell align="center"><Box component="span" sx={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width: 24, height: 24, borderRadius: "50%", backgroundColor: "#e8f7ee", color: "#1f9d61" }}><CheckCircleIcon sx={{ fontSize: 18 }} /></Box></TableCell>
                          <TableCell align="center"><Box component="span" sx={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width: 24, height: 24, borderRadius: "50%", backgroundColor: "#e8f7ee", color: "#1f9d61" }}><CheckCircleIcon sx={{ fontSize: 18 }} /></Box></TableCell>
                          <TableCell align="center"><Box component="span" sx={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width: 24, height: 24, borderRadius: "50%", backgroundColor: "#fdecec", color: "#c62828" }}><CancelIcon sx={{ fontSize: 18 }} /></Box></TableCell>
                          <TableCell align="center"><Box component="span" sx={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width: 24, height: 24, borderRadius: "50%", backgroundColor: "#fdecec", color: "#c62828" }}><CancelIcon sx={{ fontSize: 18 }} /></Box></TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>View Client Teams</TableCell>
                          <TableCell align="center"><Box component="span" sx={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width: 24, height: 24, borderRadius: "50%", backgroundColor: "#e8f7ee", color: "#1f9d61" }}><CheckCircleIcon sx={{ fontSize: 18 }} /></Box></TableCell>
                          <TableCell align="center"><Box component="span" sx={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width: 24, height: 24, borderRadius: "50%", backgroundColor: "#e8f7ee", color: "#1f9d61" }}><CheckCircleIcon sx={{ fontSize: 18 }} /></Box></TableCell>
                          <TableCell align="center"><Box component="span" sx={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width: 24, height: 24, borderRadius: "50%", backgroundColor: "#e8f7ee", color: "#1f9d61" }}><CheckCircleIcon sx={{ fontSize: 18 }} /></Box></TableCell>
                          <TableCell align="center"><Box component="span" sx={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width: 24, height: 24, borderRadius: "50%", backgroundColor: "#fdecec", color: "#c62828" }}><CancelIcon sx={{ fontSize: 18 }} /></Box></TableCell>
                          <TableCell align="center"><Box component="span" sx={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width: 24, height: 24, borderRadius: "50%", backgroundColor: "#fdecec", color: "#c62828" }}><CancelIcon sx={{ fontSize: 18 }} /></Box></TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>View Client Users</TableCell>
                          <TableCell align="center"><Box component="span" sx={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width: 24, height: 24, borderRadius: "50%", backgroundColor: "#e8f7ee", color: "#1f9d61" }}><CheckCircleIcon sx={{ fontSize: 18 }} /></Box></TableCell>
                          <TableCell align="center"><Box component="span" sx={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width: 24, height: 24, borderRadius: "50%", backgroundColor: "#e8f7ee", color: "#1f9d61" }}><CheckCircleIcon sx={{ fontSize: 18 }} /></Box></TableCell>
                          <TableCell align="center"><Box component="span" sx={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width: 24, height: 24, borderRadius: "50%", backgroundColor: "#e8f7ee", color: "#1f9d61" }}><CheckCircleIcon sx={{ fontSize: 18 }} /></Box></TableCell>
                          <TableCell align="center"><Box component="span" sx={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width: 24, height: 24, borderRadius: "50%", backgroundColor: "#fdecec", color: "#c62828" }}><CancelIcon sx={{ fontSize: 18 }} /></Box></TableCell>
                          <TableCell align="center"><Box component="span" sx={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width: 24, height: 24, borderRadius: "50%", backgroundColor: "#fdecec", color: "#c62828" }}><CancelIcon sx={{ fontSize: 18 }} /></Box></TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Manage Team Members</TableCell>
                          <TableCell align="center"><Box component="span" sx={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width: 24, height: 24, borderRadius: "50%", backgroundColor: "#e8f7ee", color: "#1f9d61" }}><CheckCircleIcon sx={{ fontSize: 18 }} /></Box></TableCell>
                          <TableCell align="center"><Box component="span" sx={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width: 24, height: 24, borderRadius: "50%", backgroundColor: "#e8f7ee", color: "#1f9d61" }}><CheckCircleIcon sx={{ fontSize: 18 }} /></Box></TableCell>
                          <TableCell align="center"><Box component="span" sx={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width: 24, height: 24, borderRadius: "50%", backgroundColor: "#fdecec", color: "#c62828" }}><CancelIcon sx={{ fontSize: 18 }} /></Box></TableCell>
                          <TableCell align="center"><Box component="span" sx={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width: 24, height: 24, borderRadius: "50%", backgroundColor: "#fdecec", color: "#c62828" }}><CancelIcon sx={{ fontSize: 18 }} /></Box></TableCell>
                          <TableCell align="center"><Box component="span" sx={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width: 24, height: 24, borderRadius: "50%", backgroundColor: "#fdecec", color: "#c62828" }}><CancelIcon sx={{ fontSize: 18 }} /></Box></TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Change Team Settings</TableCell>
                          <TableCell align="center"><Box component="span" sx={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width: 24, height: 24, borderRadius: "50%", backgroundColor: "#e8f7ee", color: "#1f9d61" }}><CheckCircleIcon sx={{ fontSize: 18 }} /></Box></TableCell>
                          <TableCell align="center"><Box component="span" sx={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width: 24, height: 24, borderRadius: "50%", backgroundColor: "#e8f7ee", color: "#1f9d61" }}><CheckCircleIcon sx={{ fontSize: 18 }} /></Box></TableCell>
                          <TableCell align="center"><Box component="span" sx={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width: 24, height: 24, borderRadius: "50%", backgroundColor: "#fdecec", color: "#c62828" }}><CancelIcon sx={{ fontSize: 18 }} /></Box></TableCell>
                          <TableCell align="center"><Box component="span" sx={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width: 24, height: 24, borderRadius: "50%", backgroundColor: "#fdecec", color: "#c62828" }}><CancelIcon sx={{ fontSize: 18 }} /></Box></TableCell>
                          <TableCell align="center"><Box component="span" sx={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width: 24, height: 24, borderRadius: "50%", backgroundColor: "#fdecec", color: "#c62828" }}><CancelIcon sx={{ fontSize: 18 }} /></Box></TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Manage Client Users</TableCell>
                          <TableCell align="center"><Box component="span" sx={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width: 24, height: 24, borderRadius: "50%", backgroundColor: "#e8f7ee", color: "#1f9d61" }}><CheckCircleIcon sx={{ fontSize: 18 }} /></Box></TableCell>
                          <TableCell align="center"><Box component="span" sx={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width: 24, height: 24, borderRadius: "50%", backgroundColor: "#fdecec", color: "#c62828" }}><CancelIcon sx={{ fontSize: 18 }} /></Box></TableCell>
                          <TableCell align="center"><Box component="span" sx={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width: 24, height: 24, borderRadius: "50%", backgroundColor: "#fdecec", color: "#c62828" }}><CancelIcon sx={{ fontSize: 18 }} /></Box></TableCell>
                          <TableCell align="center"><Box component="span" sx={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width: 24, height: 24, borderRadius: "50%", backgroundColor: "#fdecec", color: "#c62828" }}><CancelIcon sx={{ fontSize: 18 }} /></Box></TableCell>
                          <TableCell align="center"><Box component="span" sx={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width: 24, height: 24, borderRadius: "50%", backgroundColor: "#fdecec", color: "#c62828" }}><CancelIcon sx={{ fontSize: 18 }} /></Box></TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Change Client Settings</TableCell>
                          <TableCell align="center"><Box component="span" sx={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width: 24, height: 24, borderRadius: "50%", backgroundColor: "#e8f7ee", color: "#1f9d61" }}><CheckCircleIcon sx={{ fontSize: 18 }} /></Box></TableCell>
                          <TableCell align="center"><Box component="span" sx={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width: 24, height: 24, borderRadius: "50%", backgroundColor: "#fdecec", color: "#c62828" }}><CancelIcon sx={{ fontSize: 18 }} /></Box></TableCell>
                          <TableCell align="center"><Box component="span" sx={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width: 24, height: 24, borderRadius: "50%", backgroundColor: "#fdecec", color: "#c62828" }}><CancelIcon sx={{ fontSize: 18 }} /></Box></TableCell>
                          <TableCell align="center"><Box component="span" sx={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width: 24, height: 24, borderRadius: "50%", backgroundColor: "#fdecec", color: "#c62828" }}><CancelIcon sx={{ fontSize: 18 }} /></Box></TableCell>
                          <TableCell align="center"><Box component="span" sx={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width: 24, height: 24, borderRadius: "50%", backgroundColor: "#fdecec", color: "#c62828" }}><CancelIcon sx={{ fontSize: 18 }} /></Box></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </StyledAccordion>
                <StyledAccordion title="What are layouts? How do I select one?" categories={["website"]}>
                  <Typography variant="body1">
                    Layouts control how the meeting interface is arranged for you. There are currently two layouts:
                  </Typography>
                  <Typography variant="body1">
                    <Box component="span" sx={{ fontWeight: 700 }}>Metro Layout</Box> - suitable for specialists and teams in centralised locations.
                  </Typography>
                  <Typography variant="body1">
                    <Box component="span" sx={{ fontWeight: 700 }}>Remote Layout</Box> - suitable for in-person meetings, when used in conjunction with a headset and microphones.
                  </Typography>
                  <Typography variant="body1" component="div" sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                    If your team has more than one layout enabled, you will be asked to choose a layout when you join a meeting. <RoleChip role="Team Admin" /> users can control which layouts are available for a team from the team's <UiActionLabel>Settings</UiActionLabel> tab, under "Meeting Layouts".
                  </Typography>
                </StyledAccordion>
              </>
            )}
            {isCategoryVisible("headset") && (
              <>
                <StyledAccordion title="How can I find eye dominance?" categories={["headset"]}>
                  <Typography variant="body1">
                    {`First, it is important to determine your dominant eye. Viewing the headset display with your dominant eye will allow for the most clear and comfortable use of the device.`}
                  </Typography>
                  <Typography variant="body1">
                    {`Eye dominance is the tendency to prefer visual input from one eye to the other. It is best to use your dominant eye when viewing the display.`}
                  </Typography>
                  <NoteBox>
                    {`Eye dominance is not related to right- or left-handedness.`}
                  </NoteBox>
                  <Typography variant="body1">
                    {`To determine which eye is dominant, do the following:`}
                  </Typography>
                  <NumberedStep number={1}>
                    {`Form a triangle with your hands placed together at arm's length.`}
                  </NumberedStep>
                  <CaptionedImage
                    src="https://support.realwear.com/hs-fs/hubfs/RealWear%20Navigator%E2%84%A2%20500/3.1.jpg?width=500&name=3.1.jpg"
                    alt="Forming a triangle with your hands"
                    caption="Forming a triangle with your hands"
                  />
                  <NumberedStep number={2}>
                    {`With both eyes open, focus on a distant object centered in the triangle.`}
                  </NumberedStep>
                  <NumberedStep number={3}>
                    {`Maintain focus on the object centered in the triangle and keep your head and both hands stationary.`}
                  </NumberedStep>
                  <NumberedStep number={4}>
                    {`Close your right eye. If the object is still in the triangle, you are left-eye dominant.`}
                  </NumberedStep>
                  <NumberedStep number={5}>
                    {`Repeat step 3 and close your left eye. If the object is still in the triangle, you are right-eye dominant.`}
                  </NumberedStep>
                  <NoteBox>
                    {`If the object is in the triangle with either eye, then you are dominant eye neutral.`}
                  </NoteBox>
                  <CaptionedImage
                    src="https://support.realwear.com/hs-fs/hubfs/RealWear%20Navigator%E2%84%A2%20500/3.2.jpg?width=650&name=3.2.jpg"
                    alt="Testing eye dominance with each eye closed"
                    caption="Testing eye dominance with each eye closed"
                  />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Your Dominant Eye Configuration
                  </Typography>
                  <Typography variant="body1">
                    {`After your dominant eye is determined, set up the headset so the display and camera are aligned with the dominant eye side of your head.`}
                  </Typography>
                  <Typography variant="body1">
                    {`If the display and camera are not positioned on your dominant eye side, do the following:`}
                  </Typography>
                  <NumberedStep number={1}>
                    {`Place the headset on a flat surface.`}
                  </NumberedStep>
                  <NumberedStep number={2}>
                    {`Raise the boom arm above the camera and flip the headset over.`}
                  </NumberedStep>
                  <NumberedStep number={3}>
                    {`Bend the boom arm so the display is positioned below the camera on your dominant eye side.`}
                  </NumberedStep>
                  <CaptionedImage
                    src="https://support.realwear.com/hs-fs/hubfs/RealWear%20Navigator%E2%84%A2%20500/3.3.jpg?width=430&name=3.3.jpg"
                    alt="Boom arm positioned on the dominant eye side"
                    caption="Boom arm positioned on the dominant eye side"
                  />
                </StyledAccordion>
                <StyledAccordion title="What do I do if I can't verify the headset's WIFI connection?" categories={["headset"]}>
                  <Typography variant="body1">
                    If you cannot verify your connection, this means that the RealWear headset is not connecting to the SAH-C Wi-Fi.
                  </Typography>
                  <NumberedStep number={1}>
                    {`Ensure that the Wi-Fi is enabled on the headset and that you are within range of the SAH-C Wi-Fi network.`}
                  </NumberedStep>
                  <NumberedStep number={2}>
                    {`If the issue persists, restart the headset and try connecting to the SAH-C Wi-Fi network again.`}
                  </NumberedStep>
                  <NumberedStep number={3}>
                    {`If the problem still persists, contact IVAI for further support.`}
                  </NumberedStep>
                </StyledAccordion>
                <StyledAccordion title="Can I wear glasses with the headset?" categories={["headset"]}>
                  <Typography variant="body1">
                    {`It is recommended that if you have prescription glasses to wear them with the headset. `}
                  </Typography>
                </StyledAccordion>
                <StyledAccordion title="The power light is blinking." categories={["headset"]}>
                  <Typography variant="body1">
                    {`The power light may blink either green, orange or red to indicate how full the battery is.`}
                  </Typography>
                </StyledAccordion>
                <StyledAccordion title="The video is upside down." categories={["headset"]}>
                  <Typography variant="body1">
                    {`The headset is designed to be worn on either eye. When changing which eye the headset is worn on the camera can become stuck upside down. To fix this restart the device. It is important to do this rather than switching which eye the headset is on.`}
                  </Typography>
                </StyledAccordion>
                <StyledAccordion title="There is a black screen and I can't tell if the device is on." categories={["headset"]}>
                  <Typography variant="body1">{`Try the following: `}</Typography>
                  <NumberedStep number={1}>
                    {`Check that the screen is properly aligned with your eyes. It can appear black if the only part of the screen you can see is black.`}
                  </NumberedStep>
                  <NumberedStep number={2}>
                    Try the voice command <VoiceCommandLabel>navigate home</VoiceCommandLabel> to return to the home screen. Ensure that voice commands are active by pressing the action button.
                  </NumberedStep>
                  <NumberedStep number={3}>
                    {`Check that the device is not asleep. If the device is asleep tap the power button to wake it.`}
                  </NumberedStep>
                  <NumberedStep number={4}>
                    {`The device may be off. Hold the power button for 5 seconds to turn it on.`}
                  </NumberedStep>
                  <NumberedStep number={5}>
                    {`The device may be out of battery. Place on charge and then try turning it on.`}
                  </NumberedStep>
                </StyledAccordion>
                <StyledAccordion title="What do I do if I can see the keyboard on the Headset?" categories={["headset"]}>
                  <CaptionedImage src={KeyBoard} alt="The keyboard on the headset" caption="The keyboard on the headset" />
                  <Typography variant="body1">
                    If the headset is not muted, it is easy to accidentally select the navigation bar of the website and open the keyboard.
                    </Typography>
                    <NumberedStep number={1}>
                      Say <VoiceCommandLabel>navigate back</VoiceCommandLabel> to go to the previous page.
                      </NumberedStep>
                      <NumberedStep number={2}>
                        If that does not bring you to the call page you can say <VoiceCommandLabel>navigate home</VoiceCommandLabel> to return the home menu.”
                      </NumberedStep>
                  <NoteBox>
                    To prevent accidental commands, use the voice command <VoiceCommandLabel>mute microphone</VoiceCommandLabel> which will stop voice commands until the action button is pressed.
                  </NoteBox>
                </StyledAccordion>
                <StyledAccordion title="The video for the headset is black or not appearing." categories={["headset"]}>
                  <CaptionedImage src={BlackScreen} alt="Black Screen" caption="Black Screen" />
                  <Typography variant="body1">
                    {`There are a few things that can cause this issue: `}
                  </Typography>
                  <NumberedStep number={1}>
                    Sometimes when granting permission to the camera it will not activate. Say <VoiceCommandLabel>disable camera</VoiceCommandLabel> and then <VoiceCommandLabel>enable camera</VoiceCommandLabel> to try and fix this issue.
                  </NumberedStep>
                  <NumberedStep number={2}>
                    The camera may be disabled. Say <VoiceCommandLabel>enable camera</VoiceCommandLabel> to reactivate the camera.
                  </NumberedStep>
                  <NumberedStep number={3}>
                    If you have navigated back or home while video is active the website may not be able to get permission to use the camera. Say <VoiceCommandLabel>disable camera</VoiceCommandLabel> and then <VoiceCommandLabel>enable camera</VoiceCommandLabel> to try and fix this issue. If this does not work say <VoiceCommandLabel>leave call</VoiceCommandLabel> and then follow the instructions to rejoin the call.
                  </NumberedStep>
                  <NumberedStep number={4}>
                    If you exited when taking a photo the camera will not automatically be turned back on. Say <VoiceCommandLabel>enable camera</VoiceCommandLabel> to reactivate the video.
                  </NumberedStep>
                </StyledAccordion>
                <StyledAccordion title="I said “upload photo” but the camera application is not opening." categories={["headset"]}>
                  <Typography variant="body1">
                    The headset will bring up a very small dialogue box asking if you want to get a local image or take a photo.
                  </Typography>
                  <NumberedStep number={1}>
                    Say <VoiceCommandLabel>My Camera</VoiceCommandLabel> to open the camera application.
                  </NumberedStep>
                  <NoteBox>
                    It is important to always use the camera as it will not store the image locally.
                  </NoteBox>
                  <CaptionedImage src={WithPopUp} alt="When prompted, choose My Camera" caption="When prompted, choose My Camera" />
                </StyledAccordion>
                <StyledAccordion title="Can I mute the microphone on the headset to prevent it from accidentally registering incorrect voice commands?" categories={["headset"]}>
                  <Typography variant="body1">
                    Yes, and you should use it when you want to prevent the headset from accidentally registering incorrect voice commands.
                  </Typography>
                  <NumberedStep number={1}>
                    You can say <VoiceCommandLabel>Mute microphone</VoiceCommandLabel> to mute the microphone on the headset.
                  </NumberedStep>
                  <NumberedStep number={2}>
                    To unmute the microphone, press the action button on the side of the headset.
                  </NumberedStep>
                  <CaptionedImage
                    src={RealWearActionButton}
                    alt="RealWear Action Button"
                    caption="The action button is located on the side of the headset."
                  />
                </StyledAccordion>
                <StyledAccordion title="The headset is not responding to voice commands." categories={["headset"]}>
                  <Typography variant="body1">
                    {`There are two main reasons why the headset may not be responding to voice commands: `}
                  </Typography>
                  <NumberedStep number={1}>
                    {`The command was not said clearly enough. Try speaking the command slower and louder.`}
                  </NumberedStep>
                  <NumberedStep number={2}>
                    {
                      "The headset may be have its microphone muted. Press the action button on the side of the headset to unmute the microphone."
                    }
                  </NumberedStep>
                  <CaptionedImage
                    src={RealWearActionButton}
                    alt="RealWear Action Button"
                    caption="The action button is located on the side of the headset."
                  />
                </StyledAccordion>
              </>
            )}
          </AccordionGroup>
        </Card>
      </Stack>
    </MainLayout>
  );
};

export default HelpPage;
